import {useEffect, useContext} from 'react';
import Section from 'components/forms/Section';
import {Grid, Typography} from '@material-ui/core';
import {DocumentCard} from 'components/documents';
import fields from '../fields';
import {useFieldData} from 'components/forms/hooks';
import {bootstrapOldForm} from 'components/forms/AutoSection';
import {LoadingPage} from 'components';
import Show from 'components/utils/Show';
import {useGetTankermans} from 'hooks/useGetEndpoint';
import {AppCtx} from 'context/AppContext';

const getSummary = doc => {
    return `${doc.loadings?.length ? `Loadings (${doc.loadings.length})\n` : ''}
    ${doc.discharges?.length ? `Discharges (${doc.discharges.length})\n` : ''}
    ${
        !doc.commencement_loadings?.length
            ? `Commencement Loadings (${doc.commencement_loadings.length})\n`
            : ''
    }
    ${
        doc.completion_loadings?.length
            ? `Completion Loadings (${doc.completion_loadings.length})\n`
            : ''
    }
    ${
        doc.commencement_discharges?.length
            ? `Commencement Discharges (${doc.commencement_discharges.length})\n`
            : ''
    }
    ${
        doc.completion_discharges?.length
            ? `Completion Discharges (${doc.completion_discharges.length})\n`
            : ''
    }`;
};

const getDates = doc => {
    return `Signed ${new Date(doc.date_signed).toLocaleDateString()} | 
    Submitted ${new Date(doc.posted_datetime).toLocaleDateString()}`;
};

const descending = (a, b) => {
    if (a.posted_datetime > b.posted_datetime) {
        return -1;
    } else if (a.posted_datetime < b.posted_datetime) {
        return 1;
    }
    return 0;
};

const AssociateTankerman = props => {
    const {schema, initialValues} = bootstrapOldForm(
        'AssociateTankerman',
        fields
    );
    const [tankerman, setTankerman] = useFieldData('user_tankerman_id');
    const [sslSigned] = useFieldData('date_document_signed');
    const {data, isLoading, error} = useGetTankermans();
    const {tankermanId} = useContext(AppCtx);

    useEffect(() => {
        if (tankermanId) {
            setTankerman(tankermanId);
        }
        //eslint-disable-next-line
    }, [tankermanId]);

    const documents = data?.sort(descending) || [];

    // If no form data, select the first ssl matching the signed date
    // We sort the tankermans in descending order of post timestamp
    // So this should be the latest entered
    useEffect(() => {
        if (!tankerman && data?.length && sslSigned) {
            // Choose the first value that matches the date
            const matchingTankerman = data.find(
                doc => doc.date_signed === sslSigned
            );
            if (matchingTankerman) {
                setTankerman(matchingTankerman.id);
            }
        }
    }, [data, sslSigned, tankerman, setTankerman]);

    return (
        <Section {...props} {...{schema, initialValues}}>
            <Typography variant='h6'>Tankerman Letter</Typography>
            <Typography variant='body1'>
                It looks like you might have a Tankerman Letter associated with
                this SSL. If so, please select it below.
            </Typography>
            <Typography variant='body1'>
                If this doesn't apply or you haven't entered it yet, select Skip
                to continue. We'll associate these when you enter your Tankerman
                Letter, if
            </Typography>
            <Show condition={isLoading}>
                <LoadingPage />
            </Show>
            <Show condition={!isLoading}>
                <Grid
                    container
                    direction='row'
                    wrap
                    spacing={2}
                    justify='center'
                >
                    {documents.map(doc => (
                        <Grid item key={`tankerman-${doc.id}`}>
                            <DocumentCard
                                document={doc}
                                title={`Tankerman Letter`}
                                subtitle={getSummary(doc)}
                                otherData={getDates(doc)}
                                onClick={() => setTankerman(doc.id)}
                                selected={doc.id === tankerman}
                                variant='listItem'
                            />
                        </Grid>
                    ))}
                    <Show condition={!error}>
                        <Grid item>
                            <DocumentCard
                                title='Skip'
                                subtitle='Not applicable OR need to enter'
                                onClick={() => setTankerman(null)}
                                selected={!tankerman}
                                variant='listItem'
                            />
                        </Grid>
                    </Show>
                    <Show condition={error}>
                        <DocumentCard
                            title='Oops!'
                            subtitle="We couldn't load your Tankermans, please reload to try again"
                            variant='listItem'
                            error
                        />
                    </Show>
                </Grid>
            </Show>
        </Section>
    );
};

export default AssociateTankerman;
